import React, { useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import { tokens } from '../../theme';
import { useChartTheme } from '../../themes/chartTheme';

export interface LeadwallTimelinePoint {
  date: string;
  variant: string;
  soi: number;
  doi: number;
}

interface Props {
  data: LeadwallTimelinePoint[];
}

const LeadwallTimelineChart: React.FC<Props> = ({ data }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const chartTheme = useChartTheme();

  const series = useMemo(() => {
    const days = Array.from(new Set(data.map((r) => r.date))).sort();
    const variants = ['A1', 'A2'];
    const out: { id: string; data: { x: string; y: number }[] }[] = [];

    for (const variant of variants) {
      const soiSerie = { id: `${variant} · SOI`, data: [] as { x: string; y: number }[] };
      const doiSerie = { id: `${variant} · DOI`, data: [] as { x: string; y: number }[] };
      for (const day of days) {
        const row = data.find((r) => r.date === day && r.variant === variant);
        soiSerie.data.push({ x: day, y: row ? row.soi : 0 });
        doiSerie.data.push({ x: day, y: row ? row.doi : 0 });
      }
      out.push(soiSerie, doiSerie);
    }
    return out;
  }, [data]);

  const colorMap: Record<string, string> = {
    'A1 · SOI': colors.blueAccent[400],
    'A1 · DOI': colors.blueAccent[700],
    'A2 · SOI': colors.greenAccent[400],
    'A2 · DOI': colors.greenAccent[700],
  };

  if (series.length === 0 || series.every((s) => s.data.length === 0)) {
    return (
      <Box height={320} display="flex" alignItems="center" justifyContent="center">
        Keine Daten für den gewählten Zeitraum.
      </Box>
    );
  }

  return (
    <Box height={320}>
      <ResponsiveLine
        data={series}
        theme={chartTheme}
        margin={{ top: 30, right: 130, bottom: 60, left: 50 }}
        xScale={{ type: 'point' }}
        yScale={{ type: 'linear', min: 0, max: 'auto', stacked: false }}
        axisBottom={{
          tickRotation: -45,
          legend: 'Datum',
          legendOffset: 50,
          legendPosition: 'middle',
        }}
        axisLeft={{ legend: 'Anzahl', legendOffset: -40, legendPosition: 'middle' }}
        colors={(serie) => colorMap[serie.id as string] ?? colors.grey[500]}
        lineWidth={2}
        pointSize={6}
        useMesh={true}
        enableSlices="x"
        legends={[
          {
            anchor: 'right',
            direction: 'column',
            translateX: 110,
            itemWidth: 100,
            itemHeight: 18,
            symbolSize: 12,
            symbolShape: 'circle',
          },
        ]}
      />
    </Box>
  );
};

export default LeadwallTimelineChart;
